import React, { useEffect, useState } from 'react';
import { Chip, Stack, SxProps, Theme } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EDITORIAL } from '../FormGenerator/editorialFormStyles';

interface Option {
    label: string;
    value: string;
}

export interface IChipGroupWithMultiSelection {
    options: Option[];
    initialSelected?: Option | Option[]; // Single or multiple initial selections
    tagSx?: SxProps<Theme>;
    onTagClick?: (tag: Option | Option[]) => void;
    clickable?: boolean;
    multiple?: boolean; // If true, allow multiple selections
    /** Pill tabs: gradient selected, surface container unselected */
    editorial?: boolean;
    variant?: 'brand' | 'default';
}

const ChipGroupWithMultiSelection: React.FC<IChipGroupWithMultiSelection> = ({
    options,
    initialSelected,
    tagSx,
    onTagClick,
    clickable,
    multiple,
    editorial,
}) => {
    const { themeStyle } = useThemeContext();
    const [selectedTags, setSelectedTags] = useState<Option[]>([]); // State for selected options

    // Preselect logic for single or multiple selections
    useEffect(() => {
        if (initialSelected) {
            if (Array.isArray(initialSelected)) {
                setSelectedTags(multiple ? initialSelected : [initialSelected[0]]);
            } else {
                setSelectedTags([initialSelected]);
            }
        } else if (options.length > 0) {
            setSelectedTags(multiple ? options : [options[0]]);
        }
    }, [initialSelected, options, multiple]);

    // Function to check if a tag is selected
    const isSelected = (tag: Option) =>
        multiple ? selectedTags.some(selected => selected.value === tag.value) : selectedTags[0]?.value === tag.value;

    // Handle tag click
    const handleTagClick = (tag: Option) => {
        if (!clickable) return;
        if (multiple) {
            const isAlreadySelected = selectedTags.some(selected => selected.value === tag.value);
            const tagSelect = isAlreadySelected
                ? selectedTags.filter(selected => selected.value !== tag.value)
                : [...selectedTags, tag];
            setSelectedTags(tagSelect);
            onTagClick && onTagClick(tagSelect);
        } else {
            setSelectedTags(prev => (prev[0]?.value === tag.value ? [] : [tag]));
            onTagClick && onTagClick(tag);
        }
    };

    return (
        <Stack direction="row" gap={1} position="relative" flexWrap="wrap" width="100%">
            {/* Render all options */}
            {options.map((tag, index) => (
                <Chip
                    label={tag.label}
                    variant="outlined"
                    key={tag.value}
                    size="small"
                    onClick={() => handleTagClick(tag)}
                    sx={{
                        flexGrow: 1,
                        ...(editorial
                            ? {
                                  borderRadius: '9999px',
                                  border: 'none',
                                  borderColor: 'transparent',
                                  fontWeight: 700,
                                  fontFamily: FONT_ENUM.MAIN_BOLD,
                                  fontSize: 13,
                                  height: 48,
                                  textTransform: 'none',
                                  cursor: clickable ? 'pointer' : 'default',
                                  backgroundColor: isSelected(tag) ? 'transparent' : EDITORIAL.surfaceLow,
                                  background: isSelected(tag)
                                      ? 'linear-gradient(135deg, #ffc965 0%, #feb700 100%)'
                                      : undefined,
                                  color: isSelected(tag) ? '#392700' : '#adaaaa',
                                  boxShadow: isSelected(tag) ? '0 0 12px rgba(255,201,101,0.28)' : 'none',
                                  transition: 'all 0.15s ease',
                                  '&:hover': {
                                      backgroundColor: isSelected(tag) ? undefined : EDITORIAL.surfaceContainerHigh,
                                      background: isSelected(tag)
                                          ? 'linear-gradient(135deg, #ffc965 0%, #feb700 100%)'
                                          : undefined,
                                  },
                              }
                            : {
                                  backgroundColor: isSelected(tag)
                                      ? theme[themeStyle].highlight
                                      : theme[themeStyle].darkGray60,
                                  color: isSelected(tag) ? theme[themeStyle].black : theme[themeStyle].tag_white,
                                  borderRadius: '25px',
                                  borderColor: isSelected(tag) ? theme[themeStyle].form_gray : 'transparent',
                                  fontWeight: 700,
                                  textTransform: 'capitalize',
                                  fontFamily: FONT_ENUM.MAIN_BOLD,
                                  cursor: clickable ? 'pointer' : 'default',
                                  fontSize: 12,
                                  height: '50px',
                              }),
                        ...tagSx,
                    }}
                />
            ))}
        </Stack>
    );
};

export { ChipGroupWithMultiSelection };
